import { BaseUser } from '../models/UserModels';

function resolveBaseURL() {
  const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
    ? process.env.REACT_APP_API_URL.trim()
    : '';

  let base = env;
  if (!base || !/^https?:\/\//.test(base)) {
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const isLocalFront = /localhost:(3000|3001|3002|3003)/.test(origin || '');
    base = isLocalFront ? 'https://jobit-vniz.onrender.com/api' : (origin ? origin.replace(/\/+$/, '') + '/api' : 'https://jobit-vniz.onrender.com/api');
  }
  if (!/\/api(\/?$)/.test(base)) {
    base = base.replace(/\/+$/, '') + '/api';
  }
  return base.replace(/\/+$/, '');
}

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const ep = typeof endpoint === 'string' ? endpoint : '';
  const isAdminEndpoint = (e) => {
    if (!e) return false;
    return [
      /^\/admin(\/?|$)/,
      /^\/admin-auth(\/?|$)/,
      /\/admin\//,
      /^\/ads\/admin(\/?|$)/,
      /^\/ad-banners\/admin(\/?|$)/,
      /^\/users\/admin(\/?|$)/,
      /^\/jobs\/admin(\/?|$)/,
      /^\/audit(\/?|$)/,
      /^\/admin-management(\/?|$)/,
      /^\/review(\/?|$)/,
      /^\/approval(\/?|$)/
    ].some((re) => re.test(e));
  };

  const preferAdmin = isAdminEndpoint(ep);
  const userToken = localStorage.getItem('token');
  const adminToken = localStorage.getItem('adminToken');

  const selectedToken = preferAdmin ? adminToken : (userToken || adminToken);

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(selectedToken && { Authorization: `Bearer ${selectedToken}` }),
    },
  };

  try {
    const base = resolveBaseURL();
    const url = `${base}${endpoint}`;
    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    });

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // Handle non-JSON responses (HTML error pages, etc.)
      await response.text();
      const debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production')
        && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
      if (debug) console.warn('[DatabaseService] non-JSON response', { endpoint, status: response.status });

      // Dev-server 404 fallback: retry once against backend port
      if (!response.ok && response.status === 404) {
        try {
          const altBase = (typeof window !== 'undefined' ? window.location.origin.replace(':3001', ':5000') : 'http://localhost:5000').replace(/\/+$/, '') + '/api';
          const altUrl = `${altBase}${endpoint}`;
          const altResp = await fetch(altUrl, {
            ...defaultOptions,
            ...options,
            headers: {
              ...defaultOptions.headers,
              ...options.headers,
            }
          });
          const altCT = altResp.headers.get('content-type');
          if (altCT && altCT.includes('application/json')) {
            const altData = await altResp.json();
            if (altResp.ok) return altData;
            data = altData;
          } else {
            await altResp.text();
            if (altResp.ok) return { success: true };
          }
        } catch (_) { /* silent */ }
      }

      if (!response.ok) {
        throw new Error('Request failed. Please try again later.');
      }

      // Default structure for successful non-JSON responses
      data = { success: true, message: 'Request completed' };
    }
    
    if (!response.ok) {
      const serverMessage = (data && (data.message || data.error || (Array.isArray(data.errors) && data.errors[0]?.message))) || null;
      throw new Error(serverMessage || 'Request failed. Please try again later.');
    }
    
    return data;
  } catch (error) {
    const debug = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production')
      && (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === 'true');
    if (debug) console.error('[DatabaseService] request failed', { endpoint, error });
    throw error;
  }
};

// User Management Service
export class UserService {
  // Create enhanced user profile
  static async createUserProfile(uid, userData, role) {
    try {
      const baseUserData = {
        ...BaseUser,
        uid,
        email: userData.email,
        phone: userData.phone,
        role,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: role === 'jobseeker' ? 'approved' : 'pending' // Auto-approve job seekers
      };

      // Create base user document
      const userResponse = await apiCall('/users', {
        method: 'POST',
        body: JSON.stringify(baseUserData),
      });

      if (!userResponse.success) {
        throw new Error(userResponse.message || 'Failed to create user');
      }

      // Create role-specific profile document
      const profileData = {
        userId: uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...this.getDefaultProfileData(role)
      };

      const profileResponse = await apiCall(`/profiles/${role}`, {
        method: 'POST',
        body: JSON.stringify(profileData),
      });

      if (!profileResponse.success) {
        throw new Error(profileResponse.message || 'Failed to create profile');
      }

      // Create approval request if needed
      if (role !== 'jobseeker') {
        await this.createApprovalRequest({
          type: 'user_registration',
          requesterId: uid,
          requesterRole: role,
          data: { ...baseUserData, profileData: userData },
          priority: 'medium'
        });
      }

      return { success: true, uid };
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  // Get default profile data based on role
  static getDefaultProfileData(role) {
    switch (role) {
      case 'jobseeker':
        return {
          personalInfo: {},
          education: [],
          workExperience: [],
          skills: [],
          cv: {},
          preferences: {},
          applications: [],
          savedJobs: []
        };
      case 'employer':
        return {
          companyInfo: {},
          representative: {},
          documents: {},
          jobPostingPreferences: {},
          postedJobs: [],
          analytics: {
            totalJobsPosted: 0,
            totalApplicationsReceived: 0,
            averageApplicationsPerJob: 0,
            successfulHires: 0
          }
        };
      case 'serviceprovider':
        return {
          personalInfo: {},
          services: [],
          portfolio: { images: [], videos: [] },
          documents: {},
          paymentInfo: {},
          bookings: [],
          earnings: {
            totalEarnings: 0,
            pendingPayments: 0,
            completedBookings: 0,
            averageRating: 0,
            totalReviews: 0
          }
        };
      default:
        return {};
    }
  }

  // Update user profile using backend auth endpoint
  static async updateUserProfile(uid, role, profileData) {
    try {
      // Map frontend profile to backend schema before sending
      const { mapJobSeekerToBackend, mapEmployerToBackend } = await import('../utils/profileMapping');
      const isEmployer = role === 'employer' || role === 'job_poster';
      const mapped = isEmployer ? mapEmployerToBackend(profileData) : mapJobSeekerToBackend(profileData);

      // Debug: verify array lengths and payload structure before request
      try {
        const expLen = mapped?.jobSeekerProfile?.experience?.length ?? 0;
        const eduLen = mapped?.jobSeekerProfile?.education?.length ?? 0;
        const jtLen = mapped?.jobSeekerProfile?.jobPreferences?.jobTypes?.length ?? 0;
        const wlLen = mapped?.jobSeekerProfile?.jobPreferences?.workLocation?.length ?? 0;
        console.debug('[updateUserProfile] sending arrays:', { expLen, eduLen, jtLen, wlLen });
      } catch (_) { /* no-op */ }

      const profileResponse = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(mapped),
      });

      // Debug: log response hints depending on role
      try {
        const user = profileResponse?.user || {};
        if (isEmployer) {
          const hasEmp = !!user?.employerProfile;
          console.debug('[updateUserProfile] employerProfile present:', hasEmp);
        } else {
          const expLenR = user?.jobSeekerProfile?.experience?.length ?? 0;
          const eduLenR = user?.jobSeekerProfile?.education?.length ?? 0;
          console.debug('[updateUserProfile] response arrays:', { expLenR, eduLenR });
        }
      } catch (_) { /* no-op */ }

      if (!profileResponse.success) {
        throw new Error(profileResponse.message || 'Failed to update profile');
      }

      // Compute completion locally; backend may not have /users/:id PUT
      // Keep this logic in case callers need completion status
      const isCompleted = this.checkProfileCompletion(role, profileData);

      return { success: true, user: profileResponse.user, profileCompleted: isCompleted };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Check if profile is complete based on role requirements
  static checkProfileCompletion(role, profileData) {
    switch (role) {
      case 'jobseeker':
        return !!(profileData.personalInfo?.firstName && 
                 profileData.personalInfo?.lastName &&
                 profileData.personalInfo?.dateOfBirth &&
                 profileData.education?.length > 0);
      case 'employer':
        // Support both old companyInfo shape and wizard-flat shape
        const ci = profileData.companyInfo || {};
        const companyName = profileData.companyName || ci.companyName;
        const industry = profileData.industry || ci.industry;
        const representativeName = profileData.representativeName || profileData.representative?.name;
        return !!(companyName && industry && representativeName);
      case 'serviceprovider':
        return !!(profileData.personalInfo?.firstName &&
                 profileData.personalInfo?.lastName &&
                 profileData.services?.length > 0);
      default:
        return false;
    }
  }

  // Get current authenticated user profile
  static async getUserProfile(uid) {
    try {
      // Correct path: backend exposes /api/auth/profile
      const res = await apiCall('/auth/profile');
      const user = res?.user || res;
      if (!user) {
        throw new Error('User not found');
      }
      return user;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  // Get users by role with pagination
  static async getUsersByRole(role, pageSize = 10, lastDoc = null) {
    try {
      let url = `/users/role/${role}?limit=${pageSize}`;
      if (lastDoc) {
        url += `&after=${lastDoc.id}`;
      }

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch users');
      }

      return {
        users: response.users,
        lastDoc: response.lastDoc,
        hasMore: response.hasMore
      };
    } catch (error) {
      console.error('Error fetching users by role:', error);
      throw error;
    }
  }

  // Update user status (approve/reject)
  static async updateUserStatus(uid, status, reviewNotes = '') {
    try {
      const response = await apiCall(`/users/${uid}/status`, {
        method: 'PUT',
        body: JSON.stringify({
          status,
          reviewNotes,
          statusUpdatedAt: new Date().toISOString(),
          reviewedBy: 'admin', // This should come from current admin user
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user status');
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Create approval request
  static async createApprovalRequest(requestData) {
    try {
      const approvalRequest = {
        ...requestData,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        reviewedBy: null,
        reviewedAt: null,
        reviewNotes: ''
      };

      const response = await apiCall('/approval-requests', {
        method: 'POST',
        body: JSON.stringify(approvalRequest),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create approval request');
      }

      return response.request;
    } catch (error) {
      console.error('Error creating approval request:', error);
      throw error;
    }
  }

  // Get pending approval requests
  static async getPendingApprovalRequests(type = null, pageSize = 10) {
    try {
      let url = `/approval-requests/pending?limit=${pageSize}`;
      if (type) {
        url += `&type=${type}`;
      }

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch approval requests');
      }

      return response.requests;
    } catch (error) {
      console.error('Error fetching approval requests:', error);
      throw error;
    }
  }

  // Process approval request
  static async processApprovalRequest(requestId, decision, reviewNotes = '') {
    try {
      const response = await apiCall(`/approval-requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: decision,
          reviewedAt: new Date().toISOString(),
          reviewNotes,
          reviewedBy: 'admin' // This should come from current admin user
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to process approval request');
      }

      return response.request;
    } catch (error) {
      console.error('Error processing approval request:', error);
      throw error;
    }
  }
}

// Database Service Class
export class DatabaseService {
  // Get current authenticated user profile
  static async getUserProfile(uid) {
    try {
      // Correct path: backend exposes /api/auth/profile
      const res = await apiCall('/auth/profile');
      const user = res?.user || res;
      if (!user) {
        throw new Error('User not found');
      }
      return user;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  // Get users by role with pagination
  static async getUsersByRole(role, pageSize = 10, lastDoc = null) {
    try {
      let url = `/users/role/${role}?limit=${pageSize}`;
      if (lastDoc) {
        url += `&after=${lastDoc.id}`;
      }

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch users');
      }

      return {
        users: response.users,
        lastDoc: response.lastDoc,
        hasMore: response.hasMore
      };
    } catch (error) {
      console.error('Error fetching users by role:', error);
      throw error;
    }
  }

  // Update user status (approve/reject)
  static async updateUserStatus(uid, status, reviewNotes = '') {
    try {
      const response = await apiCall(`/users/${uid}/status`, {
        method: 'PUT',
        body: JSON.stringify({
          status,
          reviewNotes,
          statusUpdatedAt: new Date().toISOString(),
          reviewedBy: 'admin', // This should come from current admin user
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user status');
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Create approval request
  static async createApprovalRequest(requestData) {
    try {
      const approvalRequest = {
        ...requestData,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        reviewedBy: null,
        reviewedAt: null,
        reviewNotes: ''
      };

      const response = await apiCall('/approval-requests', {
        method: 'POST',
        body: JSON.stringify(approvalRequest),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create approval request');
      }

      return response.request;
    } catch (error) {
      console.error('Error creating approval request:', error);
      throw error;
    }
  }

  // Get pending approval requests
  static async getPendingApprovalRequests(type = null, pageSize = 10) {
    try {
      let url = `/approval-requests/pending?limit=${pageSize}`;
      if (type) {
        url += `&type=${type}`;
      }

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch approval requests');
      }

      return response.requests;
    } catch (error) {
      console.error('Error fetching approval requests:', error);
      throw error;
    }
  }

  // Process approval request
  static async processApprovalRequest(requestId, decision, reviewNotes = '') {
    try {
      const response = await apiCall(`/approval-requests/${requestId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: decision,
          reviewedAt: new Date().toISOString(),
          reviewNotes,
          reviewedBy: 'admin' // This should come from current admin user
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to process approval request');
      }

      return response.request;
    } catch (error) {
      console.error('Error processing approval request:', error);
      throw error;
    }
  }

  // Create job posting
  static async createJobPosting(jobData) {
    try {
      const job = {
        ...jobData,
        status: 'pending',
        approvalStatus: {
          status: 'pending',
          reviewedBy: null,
          reviewedAt: null,
          reviewNotes: ''
        },
        postedAt: new Date().toISOString(),
        applicationsCount: 0,
        viewsCount: 0
      };

      const response = await apiCall('/jobs', {
        method: 'POST',
        body: JSON.stringify(job),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create job posting');
      }

      return response.job;
    } catch (error) {
      console.error('Error creating job posting:', error);
      throw error;
    }
  }

  // Update job approval status
  static async updateJobApprovalStatus(jobId, status, reviewNotes = '') {
    try {
      const response = await apiCall(`/jobs/${jobId}/approval`, {
        method: 'PUT',
        body: JSON.stringify({
          'approvalStatus.status': status,
          'approvalStatus.reviewNotes': reviewNotes,
          'approvalStatus.reviewedAt': new Date().toISOString(),
          'approvalStatus.reviewedBy': 'admin',
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update job approval status');
      }

      return response.job;
    } catch (error) {
      console.error('Error updating job approval status:', error);
      throw error;
    }
  }

  // Get jobs with filters and pagination
  static async getJobs(filters = {}, pageSize = 10, lastDoc = null) {
    try {
      let url = `/jobs?limit=${pageSize}`;
      
      if (filters.category) url += `&category=${filters.category}`;
      if (filters.location) url += `&location=${filters.location}`;
      if (filters.employmentType) url += `&employmentType=${filters.employmentType}`;
      if (lastDoc) url += `&after=${lastDoc.id}`;

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch jobs');
      }

      return {
        jobs: response.jobs,
        lastDoc: response.lastDoc,
        hasMore: response.hasMore
      };
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }

  // Create service
  static async createService(serviceData) {
    try {
      const service = {
        ...serviceData,
        status: 'pending',
        approvalStatus: {
          status: 'pending',
          reviewedBy: null,
          reviewedAt: null,
          reviewNotes: ''
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        rating: 0,
        reviewCount: 0,
        bookingsCount: 0
      };

      const response = await apiCall('/services', {
        method: 'POST',
        body: JSON.stringify(service),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create service');
      }

      return response.service;
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  }

  // Update service approval status
  static async updateServiceApprovalStatus(serviceId, status, reviewNotes = '') {
    try {
      const response = await apiCall(`/services/${serviceId}/approval`, {
        method: 'PUT',
        body: JSON.stringify({
          'approvalStatus.status': status,
          'approvalStatus.reviewNotes': reviewNotes,
          'approvalStatus.reviewedAt': new Date().toISOString(),
          'approvalStatus.reviewedBy': 'admin',
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update service approval status');
      }

      return response.service;
    } catch (error) {
      console.error('Error updating service approval status:', error);
      throw error;
    }
  }

  // Create notification
  static async createNotification(notificationData) {
    try {
      const notification = {
        ...notificationData,
        read: false,
        createdAt: new Date().toISOString(),
        readAt: null
      };

      const response = await apiCall('/notifications', {
        method: 'POST',
        body: JSON.stringify(notification),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create notification');
      }

      return response.notification;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  // Get user notifications
  static async getUserNotifications(userId, pageSize = 20) {
    try {
      const response = await apiCall(`/notifications/user/${userId}?limit=${pageSize}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch notifications');
      }

      return response.notifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  // Mark notification as read
  static async markNotificationAsRead(notificationId) {
    try {
      const response = await apiCall(`/notifications/${notificationId}/read`, {
        method: 'PUT',
        body: JSON.stringify({
          read: true,
          readAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to mark notification as read');
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Get unread notifications count
  static async getUnreadNotificationsCount(userId) {
    try {
      const response = await apiCall(`/notifications/user/${userId}/unread-count`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch unread count');
      }

      return response.count;
    } catch (error) {
      console.error('Error fetching unread notifications count:', error);
      throw error;
    }
  }

  // Create job application
  static async createJobApplication(applicationData) {
    try {
      const application = {
        ...applicationData,
        status: 'pending',
        appliedAt: new Date().toISOString(),
        statusUpdatedAt: new Date().toISOString(),
        reviewNotes: ''
      };

      const response = await apiCall('/applications', {
        method: 'POST',
        body: JSON.stringify(application),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create application');
      }

      return response.application;
    } catch (error) {
      console.error('Error creating job application:', error);
      throw error;
    }
  }

  // Update application status
  static async updateApplicationStatus(applicationId, status, reviewNotes = '') {
    try {
      const response = await apiCall(`/applications/${applicationId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status,
          statusUpdatedAt: new Date().toISOString(),
          reviewNotes
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update application status');
      }

      return response.application;
    } catch (error) {
      console.error('Error updating application status:', error);
      throw error;
    }
  }

  // Create service booking
  static async createServiceBooking(bookingData) {
    try {
      const booking = {
        status: 'pending',
        paymentStatus: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...bookingData, // This should come after defaults so it can override them
      };

      const response = await apiCall('/bookings', {
        method: 'POST',
        body: JSON.stringify(booking),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create booking');
      }

      return response.booking;
    } catch (error) {
      console.error('Error creating service booking:', error);
      throw error;
    }
  }

  // Update booking status
  static async updateBookingStatus(bookingId, status) {
    try {
      const response = await apiCall(`/bookings/${bookingId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status,
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update booking status');
      }

      return response.booking;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  }

  // Admin functions
  static async getPendingUsers() {
    try {
      const response = await apiCall('/admin/users/pending');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch pending users');
      }

      return response.users;
    } catch (error) {
      console.error('Error fetching pending users:', error);
      throw error;
    }
  }

  static async getPendingJobs() {
    try {
      const response = await apiCall('/admin/jobs/pending');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch pending jobs');
      }

      return response.jobs;
    } catch (error) {
      console.error('Error fetching pending jobs:', error);
      throw error;
    }
  }

  static async getPendingServices() {
    try {
      const response = await apiCall('/admin/services/pending');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch pending services');
      }

      return response.services;
    } catch (error) {
      console.error('Error fetching pending services:', error);
      throw error;
    }
  }

  static async approveUser(userId) {
    try {
      const response = await apiCall(`/admin/users/${userId}/approve`, {
        method: 'PUT',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to approve user');
      }

      return { success: true };
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    }
  }

  static async approveJob(jobId) {
    try {
      const response = await apiCall(`/admin/jobs/${jobId}/approve`, {
        method: 'PUT',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to approve job');
      }

      return { success: true };
    } catch (error) {
      console.error('Error approving job:', error);
      throw error;
    }
  }

  static async approveService(serviceId) {
    try {
      const response = await apiCall(`/admin/services/${serviceId}/approve`, {
        method: 'PUT',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to approve service');
      }

      return { success: true };
    } catch (error) {
      console.error('Error approving service:', error);
      throw error;
    }
  }

  // Payment functions
  static async createPayment(paymentData) {
    try {
      const response = await apiCall('/payments', {
        method: 'POST',
        body: JSON.stringify({
          ...paymentData,
          createdAt: new Date().toISOString(),
          status: 'pending'
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create payment');
      }

      return response.payment;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  static async getUserPayments(userId) {
    try {
      const response = await apiCall(`/payments/user/${userId}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch user payments');
      }

      return response.payments;
    } catch (error) {
      console.error('Error fetching user payments:', error);
      throw error;
    }
  }

  static async updatePaymentStatus(paymentId, status) {
    try {
      const response = await apiCall(`/payments/${paymentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status,
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update payment status');
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }

  static async savePaymentRecord(paymentData) {
    try {
      const response = await apiCall('/payments/save-record', {
        method: 'POST',
        body: JSON.stringify(paymentData),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to save payment record');
      }

      return response.payment;
    } catch (error) {
      console.error('Error saving payment record:', error);
      throw error;
    }
  }

  // Notification functions
  static async addNotification(userId, notificationData) {
    try {
      const response = await apiCall('/notifications', {
        method: 'POST',
        body: JSON.stringify({
          userId,
          ...notificationData,
          createdAt: new Date().toISOString(),
          read: false
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to add notification');
      }

      return response.notification;
    } catch (error) {
      console.error('Error adding notification:', error);
      throw error;
    }
  }


  static async markAllNotificationsAsRead(userId) {
    try {
      const response = await apiCall(`/notifications/user/${userId}/mark-all-read`, {
        method: 'PUT',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to mark all notifications as read');
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  static async sendNotification(notificationData) {
    try {
      const response = await apiCall('/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          ...notificationData,
          createdAt: new Date().toISOString(),
          read: false
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to send notification');
      }

      return response.notification;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  }

  static async getNotifications(userId, limit = 50) {
    try {
      const response = await apiCall(`/notifications/${userId}?limit=${limit}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch notifications');
      }

      return response.notifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  static async markAsRead(notificationId) {
    try {
      const response = await apiCall(`/notifications/${notificationId}/read`, {
        method: 'PUT',
        body: JSON.stringify({
          read: true,
          readAt: new Date().toISOString()
        }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to mark notification as read');
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  static async markAllAsRead(userId) {
    try {
      const response = await apiCall(`/notifications/${userId}/mark-all-read`, {
        method: 'PUT',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to mark all notifications as read');
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  static async getUnreadCount(userId) {
    try {
      const response = await apiCall(`/notifications/${userId}/unread-count`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch unread count');
      }

      return response.count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  // Search functions
  static async searchJobs(searchParams) {
    try {
      let url = '/jobs/search?';
      const params = new URLSearchParams();
      
      if (searchParams.query) params.append('q', searchParams.query);
      if (searchParams.filters?.category) params.append('category', searchParams.filters.category);
      if (searchParams.filters?.employmentType) params.append('employmentType', searchParams.filters.employmentType);
      if (searchParams.filters?.location) params.append('location', searchParams.filters.location);
      if (searchParams.sortBy) params.append('sortBy', searchParams.sortBy);
      if (searchParams.sortOrder) params.append('sortOrder', searchParams.sortOrder);
      
      url += params.toString();

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to search jobs');
      }

      return response.jobs;
    } catch (error) {
      console.error('Error searching jobs:', error);
      throw error;
    }
  }

  static async searchServices(searchParams) {
    try {
      let url = '/services/search?';
      const params = new URLSearchParams();
      
      if (searchParams.query) params.append('q', searchParams.query);
      if (searchParams.filters?.category) params.append('category', searchParams.filters.category);
      if (searchParams.filters?.serviceType) params.append('serviceType', searchParams.filters.serviceType);
      if (searchParams.filters?.location) params.append('location', searchParams.filters.location);
      if (searchParams.sortBy) params.append('sortBy', searchParams.sortBy);
      if (searchParams.sortOrder) params.append('sortOrder', searchParams.sortOrder);
      
      url += params.toString();

      const response = await apiCall(url);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to search services');
      }

      return response.services;
    } catch (error) {
      console.error('Error searching services:', error);
      throw error;
    }
  }

  // Get pending user approvals
  static async getPendingUserApprovals() {
    try {
      const response = await apiCall('/approval/pending/users');
      return response.users || [];
    } catch (error) {
      console.error('Error fetching pending user approvals:', error);
      throw error;
    }
  }

  // Get pending job approvals
  static async getPendingJobApprovals() {
    try {
      const response = await apiCall('/approval/pending/jobs');
      return response.jobs || [];
    } catch (error) {
      console.error('Error fetching pending job approvals:', error);
      throw error;
    }
  }

  // Get pending service approvals
  static async getPendingServiceApprovals() {
    try {
      const response = await apiCall('/approval/pending/services');
      return response.services || [];
    } catch (error) {
      console.error('Error fetching pending service approvals:', error);
      throw error;
    }
  }

  // Update user approval status
  static async updateUserApprovalStatus(userId, approvalData) {
    try {
      const response = await apiCall(`/approval/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(approvalData),
      });
      return response;
    } catch (error) {
      console.error('Error updating user approval status:', error);
      throw error;
    }
  }
}

export default DatabaseService;